import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Storage } from '@ionic/storage';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { AppConfig, GeneralState } from '../../interfaces/biozoom-api/app-configuration';
import { fillString, getApiUrl } from '../../methods/helpers';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(
    private http: HttpClient
  ) {
  }

  /**
   * map a single media-url to the url of the file behind it by providing a mapping-key
   * @param baseURL url or the media or the media directory
   * @param key key of the file within the directory
   */
  async getURLOne(baseURL: string, key: string): Promise<undefined | { loc: string[], alternateName?: string, type?: string }> {
    const url = `${getApiUrl()}/config/media/locations/${baseURL}?key=${key}`;
    let r;
    try {
      r = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json; charset=UTF-8',
            'Content-Type': 'application/json',
          },
        }
      ).catch(err => console.log(`${err}`));
    } catch (err) {
    }
    if (!r || r.status !== 200) {
      return;
    }
    const res: Record<string, { loc: string[], alternateName?: string, type?: string }> = await r.json();
    if (!(res[baseURL])) {
      return;
    }
    return res[baseURL];
  }

  /**
   * extract all media-urls from the given config file
   * skip those that are used for iframes, as those can not be pre-fetched
   * @param config config object for the application
   * @param state startup value store used to resolve media placeholders
   */
  getURLList(config: Record<string, AppConfig>, state: GeneralState) {
    let urls: string[] = [];
    for (const page of Object.values(config)) {
      const elements = page.design.elements ?? {};
      for (const element of Object.values(elements)) {
        if (element.elementType === 'iframe' || element.elementType === 'iframePost') {
          continue;
        }
        if (element.resource) {
          urls.push(this.resolveMediaURL(element.resource, state));
        } else if (element.design) {
          // else if here since some outdated resources still have the 'design'-key set to deprecated urls,
          // while having their proper url stored as 'resource'
          urls.push(this.resolveMediaURL(element.design, state));
        }
        if (element.background?.image && element.background.image !== '' && element.background.image !== 'none') {
          urls.push(this.resolveMediaURL(element.background.image, state));
        }
      }
      if (page.design.backgroundType === 'image') {
        urls.push(this.resolveMediaURL(page.design.background, state));
      }
    }
    urls = urls.filter(el => !!el).filter((v, i, a) => a.indexOf(v) === i);
    return urls;
  }

  private resolveMediaURL(url: string, state: GeneralState): string | undefined {
    const placeholders = url.match(/\$\{[^}]+\}/g) ?? [];
    for (const placeholder of placeholders) {
      let value: any = state;
      for (const key of placeholder.slice(2, -1).split('.')) {
        if (value === undefined || value === null || typeof value !== 'object' || !(key in value)) {
          return;
        }
        value = value[key];
      }
      if (value === undefined || value === null) {
        return;
      }
    }
    return fillString(url, state);
  }

  /**
   * send a list of media names, as well as the currently used language, to the api server
   * retrieve a list of mapped urls, with a list of servers each of them can be accessed from,
   * as well as an alternate url, which denotes the url of the file within a media directory
   * @param names array of media names
   * @param language currently selected user language tag
   */
  async getURLMapping(names: string[], language: string) {
    let r;
    const namesFiltered = names.filter((n) => n !== '' && !(n.includes('/')));
    if (namesFiltered.length === 0) {
      return {};
    }
    const url = `${getApiUrl()}/config/media/locations/${namesFiltered}?key=${language}`;
    try {
      r = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json; charset=UTF-8',
            'Content-Type': 'application/json',
          },
        }
      ).catch(err => console.log(`${err}`));
    } catch (err) {
    }
    if (!r || r.status !== 200) {
      return {};
    }
    const res: Record<string, { loc: string[], alternateName?: string, type?: string }> = await r.json();
    return res;
  }


  /**
   * fetch a single file from a media server via GET-request, and report the progress as an observable
   * @param url url to fetch the media from
   */
  download(url: string): Observable<HttpEvent<ArrayBuffer>> {
    return this.http.get(
      url,
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json; charset=UTF-8',
          'Content-Type': 'application/json',
          // 'Cache-Control':  'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
          // Accept: 'application/json; charset=UTF-8',
          // 'Content-Type': 'application/json',
        },
        responseType: 'arraybuffer',
        reportProgress: true,
        observe: 'events'
      },
    );

  }

  /**
   * fetch a single file from a media server via GET-request with an async call, and return the stringified file content
   * @param url url to fetch the media from
   */
  async downloadOne(url: string): Promise<string[]> {
    const res = await fetch(
      url,
      {
        method: 'GET',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json; charset=UTF-8',
          'Content-Type': 'application/json',
        }
      },
    );

    if (res.status !== 200) {
      return [];
    }
    const arrayBuffer = await res.arrayBuffer();
    return this.stringify(url, arrayBuffer);

  }

  /**
   * transform a fetched media file into a storable- and displayable string
   * @param url url the image was fetched with, used to identify the file's type
   * @param b arraybuffer containing the file's raw content
   */
  stringify(url: string, b: ArrayBuffer) {
    const a = new Uint8Array(b);
    // if (a.length > 15000000) {
    //   throw new Error();
    // }
    const res = [];
    const length = 999999;
    const chunks = Math.ceil(a.length / length);
    for (let i = 0; i < chunks; i++) {
      const b64 = this.arrayBufferToBase64(a.slice(i * length, (i + 1) * length));
      let prefix = '';
      switch (url.split('.').slice(-1)[0]) {
        case 'gif':
        {
          prefix = 'data:image/gif;base64';
          break;
        }
        case 'png':
          {
            prefix = 'data:image/png;base64';
            break;
          }
        case 'webm':
          {
            prefix = 'data:video/webm;base64';
            break;
          }
        case 'mp4':
          {
            prefix = 'data:video/mp4;base64';
            break;
          }
        case 'jpg':
        case 'jpeg': {
          prefix = 'data:image/jpeg;base64';
          break;
        }
        case 'svg': {
          prefix = 'data:image/svg+xml;base64';
          break;
        }
      }
      if (i === 0) {
        res.push(`${prefix},${b64}`);
      } else {
        res.push(b64);
      }

    }

    return res;
  }

  /**
   * transform an arraybuffer into a base64 string
   * @param buffer arraybuffer to be transformed
   */
  arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  async removeKey(url: string, urlParam: string, store: Storage) {
    const keys = await store.keys();
    for (const key of keys.filter((k) => k.startsWith(url))) {
      await store.remove(key);
    }
    for (const key of keys.filter((k) => k.startsWith(urlParam))) {
      await store.remove(key);
    }
  }
}
